import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Download,
  Eye,
  FileText,
  FileUp,
  Inbox,
  Loader2,
  Pill,
  RefreshCw,
  Send,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { farmGet, farmPatch, farmPost } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIANO_STATO = {
  valido: { label: "Valido", className: "border-emerald-200 text-emerald-700" },
  in_scadenza: { label: "In scadenza", className: "border-amber-200 text-amber-700" },
  scaduto: { label: "Scaduto", className: "border-rose-200 text-rose-700" },
  senza_scadenza: { label: "Senza scadenza", className: "border-slate-200 text-slate-600" },
};

const DOC_VALIDITA = {
  ...PIANO_STATO,
  valido_fino_a_revoca: { label: "Fino a revoca", className: "border-sky-200 text-sky-700" },
  revocato: { label: "Revocato", className: "border-slate-300 text-slate-600" },
  mancante: { label: "Mancante", className: "border-rose-200 text-rose-700" },
};

const STATO_FIRMA_OPTS = [
  { id: "non_richiesta", label: "Non richiesta" },
  { id: "da_firmare", label: "Da firmare" },
  { id: "firmato", label: "Firmato" },
  { id: "da_verificare", label: "Da verificare" },
];

const CLASSE_VALIDITA_OPTS = [
  { id: "con_scadenza", label: "Con scadenza" },
  { id: "senza_scadenza", label: "Senza scadenza" },
  { id: "valido_fino_a_revoca", label: "Valido fino a revoca" },
];

const FILTRI_STATO = [
  { id: "tutte", label: "Tutti gli ospiti" },
  { id: "da_visionare", label: "Terapia da visionare" },
  { id: "bloccata", label: "Bloccata" },
  { id: "attesa_medico", label: "Attesa medico" },
  { id: "pronta_invio", label: "Pronta invio" },
  { id: "completata", label: "Completata" },
  { id: "nessuna_pratica", label: "Nessuna pratica" },
];

const STATO_LABELS = {
  attesa_medico: { label: "Attesa Medico", className: "bg-amber-50 text-amber-700 border-amber-100" },
  pronta_invio: { label: "Pronta Invio", className: "bg-sky-50 text-sky-700 border-sky-100" },
  completata: { label: "Completata", className: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  bloccata: { label: "Bloccata", className: "bg-red-50 text-red-700 border-red-100" },
  archiviata: { label: "Archiviata", className: "bg-slate-50 text-slate-500 border-slate-200" },
  nessuna_pratica: { label: "Nessuna pratica", className: "bg-slate-50 text-slate-500 border-slate-200" },
};

function Step({ done, label, warning }) {
  const icon = done ? (
    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
  ) : warning ? (
    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
  ) : (
    <Circle className="h-3.5 w-3.5 shrink-0 text-slate-300" />
  );
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {icon}
      <span className={done ? "text-slate-700" : warning ? "text-amber-700" : "text-slate-400"}>{label}</span>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className={`rounded-xl border border-slate-200/80 p-4 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <div className="text-2xl font-semibold">{value ?? 0}</div>
      </div>
      <div className="mt-1 text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

export default function PanoramicaStruttura() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useFarmaciaAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState(() => searchParams.get("filtro") || "tutte");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(null);
  const [praticaDetail, setPraticaDetail] = useState(null);
  const [loadingPratica, setLoadingPratica] = useState(false);
  const [terapiaDetail, setTerapiaDetail] = useState(null);
  const [loadingTerapia, setLoadingTerapia] = useState(false);
  const [savingPresa, setSavingPresa] = useState(false);
  const [pianiDetail, setPianiDetail] = useState(null);
  const [loadingPiani, setLoadingPiani] = useState(false);
  const [pianiBusy, setPianiBusy] = useState(null);
  const [docsStruttura, setDocsStruttura] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsBusy, setDocsBusy] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    tipo: null,
    fascicoloId: null,
    stato_firma: "non_richiesta",
    classe_validita: "senza_scadenza",
    data_documento: "",
    scadenza: "",
    note: "",
    file: null,
    replaceId: null,
  });
  const [delegaDetail, setDelegaDetail] = useState(null);
  const [loadingDelega, setLoadingDelega] = useState(false);

  const orgId = decodeURIComponent(organizationId || "");

  const loadDocs = useCallback(async () => {
    if (!token || !orgId) return;
    setLoadingDocs(true);
    try {
      const res = await farmGet(
        `/farmacia/strutture/${encodeURIComponent(orgId)}/documenti-essenziali`,
        token,
      );
      setDocsStruttura(res);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "Errore documenti servizio");
      }
      setDocsStruttura(null);
    } finally {
      setLoadingDocs(false);
    }
  }, [token, orgId]);

  const load = useCallback(async () => {
    if (!token || !orgId) return;
    setLoading(true);
    try {
      const res = await farmGet("/farmacia/ricette/panoramica", token, { organization_id: orgId });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento panoramica");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, orgId]);

  useEffect(() => {
    load();
    loadDocs();
  }, [load, loadDocs]);

  useEffect(() => {
    const fromUrl = searchParams.get("filtro") || "tutte";
    setFiltro(fromUrl);
  }, [searchParams]);

  const ospiti = useMemo(() => {
    let list = data?.tutti_ospiti || [];
    if (filtro === "da_visionare") {
      list = list.filter((o) => o.terapia_da_visionare);
    } else if (filtro !== "tutte") {
      list = list.filter((o) => (o.stato || "nessuna_pratica") === filtro);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((o) =>
        `${o.ospite_cognome || ""} ${o.ospite_nome || ""} ${o.codice_fiscale || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    return list;
  }, [data, filtro, search]);

  const sollecita = async (o) => {
    if (!o.richiesta_id) {
      toast.error("Nessuna richiesta da sollecitare");
      return;
    }
    setSending(o.fascicolo_id);
    try {
      await farmPost("/farmacia/ricette/sollecita", token, { richiesta_ids: [o.richiesta_id] });
      toast.success("Sollecito inviato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sollecito fallito");
    } finally {
      setSending(null);
    }
  };

  const openTerapia = async (o) => {
    setLoadingTerapia(true);
    try {
      const res = await farmGet(`/farmacia/ospiti/${encodeURIComponent(o.fascicolo_id)}/terapia`, token);
      setTerapiaDetail(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento terapia");
      setTerapiaDetail(null);
    } finally {
      setLoadingTerapia(false);
    }
  };

  const registraPresaVisione = async () => {
    if (!terapiaDetail?.fascicolo_id) return;
    setSavingPresa(true);
    try {
      const res = await farmPost(
        `/farmacia/ospiti/${encodeURIComponent(terapiaDetail.fascicolo_id)}/terapia/presa-visione`,
        token,
        {}
      );
      setTerapiaDetail((prev) =>
        prev
          ? {
              ...prev,
              presa_visione: res.presa_visione || prev.presa_visione,
              storico: res.storico || prev.storico,
            }
          : prev
      );
      toast.success("Presa visione registrata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registrazione fallita");
    } finally {
      setSavingPresa(false);
    }
  };

  const openPiani = async (o) => {
    setLoadingPiani(true);
    try {
      const res = await farmGet(
        `/farmacia/ospiti/${encodeURIComponent(o.fascicolo_id)}/piani-terapeutici`,
        token
      );
      setPianiDetail(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento piani terapeutici");
      setPianiDetail(null);
    } finally {
      setLoadingPiani(false);
    }
  };

  const downloadPiano = async (piano) => {
    if (!pianiDetail?.fascicolo_id || !piano?.file_id) return;
    setPianiBusy(`dl-${piano.file_id}`);
    try {
      const res = await axios.get(
        `${API_URL}/api/farmacia/ospiti/${encodeURIComponent(pianiDetail.fascicolo_id)}/piani-terapeutici/${encodeURIComponent(piano.file_id)}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: piano.file_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = piano.file_name || piano.titolo || "piano-terapeutico.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Download fallito");
    } finally {
      setPianiBusy(null);
    }
  };

  const segnalaPianoScaduto = async (piano) => {
    if (!pianiDetail?.fascicolo_id || !piano?.file_id) return;
    setPianiBusy(`sc-${piano.file_id}`);
    try {
      await farmPost(
        `/farmacia/ospiti/${encodeURIComponent(pianiDetail.fascicolo_id)}/piani-terapeutici/${encodeURIComponent(piano.file_id)}/segnala-scaduto`,
        token,
        { note: "" }
      );
      toast.success("Segnalazione inviata alla struttura");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Segnalazione fallita");
    } finally {
      setPianiBusy(null);
    }
  };

  const richiediAggiornamentoPiani = async () => {
    if (!pianiDetail?.fascicolo_id) return;
    setPianiBusy("upd");
    try {
      await farmPost(
        `/farmacia/ospiti/${encodeURIComponent(pianiDetail.fascicolo_id)}/piani-terapeutici/richiedi-aggiornamento`,
        token,
        { note: "" }
      );
      toast.success("Richiesta di aggiornamento inviata alla struttura");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Richiesta fallita");
    } finally {
      setPianiBusy(null);
    }
  };

  const openDelega = async (o) => {
    setLoadingDelega(true);
    try {
      const res = await farmGet(
        `/farmacia/ospiti/${encodeURIComponent(o.fascicolo_id)}/documenti-essenziali`,
        token,
      );
      setDelegaDetail({
        ...res,
        ospite_nome: o.ospite_nome,
        ospite_cognome: o.ospite_cognome,
        codice_fiscale: o.codice_fiscale,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento delega");
      setDelegaDetail(null);
    } finally {
      setLoadingDelega(false);
    }
  };

  const downloadDocAuth = async (url, filename) => {
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "documento.pdf";
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const downloadDocumento = async (doc, { delega = false } = {}) => {
    if (!doc) return;
    setDocsBusy(`dl-${doc.id || "delega"}`);
    try {
      if (delega || doc.fonte === "deblistering_delega" || doc.tipo === "delega") {
        const fid = delegaDetail?.fascicolo_id || doc.fascicolo_id;
        if (!fid) throw new Error("Fascicolo mancante");
        await downloadDocAuth(
          `${API_URL}/api/farmacia/ospiti/${encodeURIComponent(fid)}/delega/download`,
          doc.file_name || "delega.pdf",
        );
      } else if (doc.id) {
        await downloadDocAuth(
          `${API_URL}/api/farmacia/documenti-essenziali/${encodeURIComponent(doc.id)}/download`,
          doc.file_name || "documento.pdf",
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download fallito");
    } finally {
      setDocsBusy(null);
    }
  };

  const submitUploadDocumento = async () => {
    if (!uploadForm.file || !uploadForm.tipo) {
      toast.error("Seleziona un file");
      return;
    }
    setDocsBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("tipo", uploadForm.tipo);
      fd.append("stato_firma", uploadForm.stato_firma);
      fd.append("classe_validita", uploadForm.classe_validita);
      if (uploadForm.data_documento) fd.append("data_documento", uploadForm.data_documento);
      if (uploadForm.classe_validita === "con_scadenza" && uploadForm.scadenza) {
        fd.append("scadenza", uploadForm.scadenza);
      }
      if (uploadForm.note) fd.append("note", uploadForm.note);

      if (uploadForm.fascicoloId) {
        const fid = uploadForm.fascicoloId;
        await farmPost(
          `/farmacia/ospiti/${encodeURIComponent(fid)}/documenti-essenziali`,
          token,
          fd,
          true,
        );
        toast.success("Delega caricata");
        const prev = delegaDetail;
        setUploadForm((f) => ({ ...f, tipo: null, fascicoloId: null, file: null }));
        if (prev?.fascicolo_id === fid) {
          await openDelega({
            fascicolo_id: fid,
            ospite_nome: prev.ospite_nome,
            ospite_cognome: prev.ospite_cognome,
            codice_fiscale: prev.codice_fiscale,
          });
        }
        await load();
      } else {
        await farmPost(
          `/farmacia/strutture/${encodeURIComponent(orgId)}/documenti-essenziali`,
          token,
          fd,
          true,
        );
        toast.success("Documento caricato");
        setUploadForm((f) => ({ ...f, tipo: null, file: null }));
        await loadDocs();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setDocsBusy(null);
    }
  };

  const registraCartaceo = async (doc) => {
    setDocsBusy(`paper-${doc.tipo}`);
    try {
      await farmPost("/farmacia/documenti-essenziali/cartaceo", token, {
        tipo: doc.tipo,
        organization_id: orgId,
        stato_firma: "firmato",
        classe_validita: "senza_scadenza",
      });
      toast.success("Documento cartaceo registrato");
      await loadDocs();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Registrazione documento cartaceo fallita");
    } finally {
      setDocsBusy(null);
    }
  };

  const richiediDocumentiOnboarding = async () => {
    setDocsBusy("onboarding");
    try {
      await farmPost(`/farmacia/strutture/${encodeURIComponent(orgId)}/richiedi-documenti`, token, {});
      toast.success("Richiesta documenti inviata alla struttura");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Invio richiesta onboarding fallito");
    } finally {
      setDocsBusy(null);
    }
  };

  const replaceDocumento = async (doc) => {
    if (!uploadForm.file || !doc?.id) {
      toast.error("Seleziona un file per sostituire");
      return;
    }
    setDocsBusy(`rp-${doc.id}`);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("stato_firma", uploadForm.stato_firma || doc.stato_firma || "non_richiesta");
      fd.append(
        "classe_validita",
        uploadForm.classe_validita || doc.classe_validita || "senza_scadenza",
      );
      if (uploadForm.data_documento) fd.append("data_documento", uploadForm.data_documento);
      if (
        (uploadForm.classe_validita || doc.classe_validita) === "con_scadenza" &&
        uploadForm.scadenza
      ) {
        fd.append("scadenza", uploadForm.scadenza);
      }
      await farmPost(
        `/farmacia/documenti-essenziali/${encodeURIComponent(doc.id)}/replace`,
        token,
        fd,
        true,
      );
      toast.success("Documento sostituito");
      setUploadForm((f) => ({ ...f, tipo: null, file: null, fascicoloId: null }));
      await loadDocs();
      if (delegaDetail) {
        await openDelega({
          fascicolo_id: delegaDetail.fascicolo_id,
          ospite_nome: delegaDetail.ospite_nome,
          ospite_cognome: delegaDetail.ospite_cognome,
          codice_fiscale: delegaDetail.codice_fiscale,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sostituzione fallita");
    } finally {
      setDocsBusy(null);
    }
  };

  const patchDocumentoMeta = async (doc, patch) => {
    if (!doc?.id) return;
    setDocsBusy(`meta-${doc.id}`);
    try {
      await farmPatch(`/farmacia/documenti-essenziali/${encodeURIComponent(doc.id)}`, token, patch);
      toast.success("Metadati aggiornati");
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    } finally {
      setDocsBusy(null);
    }
  };

  const revocaDocumento = async (doc) => {
    if (!doc?.id) return;
    if (!window.confirm("Revocare questo documento? L'operazione è registrata.")) return;
    setDocsBusy(`rv-${doc.id}`);
    try {
      await farmPost(`/farmacia/documenti-essenziali/${encodeURIComponent(doc.id)}/revoca`, token, {});
      toast.success("Documento revocato");
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoca fallita");
    } finally {
      setDocsBusy(null);
    }
  };

  const openPratica = async (o) => {
    if (!o.id || String(o.id).startsWith("nopratica_")) {
      toast.message("Nessuna pratica attiva per questo ospite");
      return;
    }
    setLoadingPratica(true);
    try {
      const res = await farmGet(`/farmacia/ricette/pratiche/${o.id}`, token, {
        organization_id: orgId,
      });
      setPraticaDetail(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento pratica");
    } finally {
      setLoadingPratica(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4 py-8 text-center">
        <p className="text-sm text-slate-600">Struttura non trovata o non autorizzata.</p>
        <Button variant="outline" onClick={() => navigate("/farmacia")}>
          Torna alla dashboard
        </Button>
      </div>
    );
  }

  const kpi = data.kpi || {};

  return (
    <div className="space-y-6">
      <FarmaciaPageChrome
        title={data.struttura_nome || "Struttura"}
        subtitle="Panoramica ospiti aderenti al deblistering e stato pratiche ricette"
        backTo="/farmacia"
        backLabel="Tutte le strutture"
      >
        <Button asChild variant="outline" size="sm" className="rounded-xl">
          <Link to={`/farmacia/richieste?organization_id=${encodeURIComponent(orgId)}`}>
            <FileUp className="mr-1.5 h-4 w-4" />
            Carepy
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="rounded-xl">
          <Link to={`/farmacia/pratiche?vista=copertura&organization_id=${encodeURIComponent(orgId)}`}>
            <ClipboardCheck className="mr-1.5 h-4 w-4" />
            Verifica ricette
          </Link>
        </Button>
      </FarmaciaPageChrome>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi icon={Users} label="Ospiti aderenti" value={kpi.ospiti_aderenti} tone="slate" />
        <Kpi icon={Eye} label="Terapie da visionare" value={kpi.terapie_da_visionare} tone="amber" />
        <Kpi icon={Inbox} label="Richieste aperte" value={kpi.richieste_aperte} tone="sky" />
        <Kpi icon={AlertTriangle} label="In scadenza" value={kpi.richieste_in_scadenza} tone="amber" />
        <Kpi icon={Send} label="Da sollecitare" value={kpi.sollecitabili} tone="rose" />
        <Kpi icon={ClipboardCheck} label="Pratiche incomplete" value={kpi.pratiche_incomplete} tone="emerald" />
      </div>

      {!data.invio_medico_abilitato && (
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          La struttura non ha abilitato l&apos;inoltro diretto ai medici dal portale. Puoi comunque caricare le
          richieste Carepy; i solleciti/inoltri al medico dipendono dalle impostazioni della struttura.
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Documenti essenziali del servizio</h2>
            <p className="text-xs text-slate-500">
              Contratto, proposta economica e privacy (nomina dati). Caricabili anche dalla struttura
              in Deblistering → Impostazioni → Contratti e Privacy.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg"
            disabled={loadingDocs}
            onClick={loadDocs}
          >
            {loadingDocs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            Aggiorna
          </Button>
          <Button size="sm" variant="outline" className="rounded-lg" disabled={docsBusy === "onboarding"} onClick={richiediDocumentiOnboarding}>
            Richiedi documenti
          </Button>
        </div>
        {loadingDocs && !docsStruttura ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            {(docsStruttura?.items || [
              { tipo: "contratto", tipo_label: "Contratto", mancante: true },
              { tipo: "proposta_economica", tipo_label: "Proposta economica", mancante: true },
              { tipo: "nomina_trattamento_dati", tipo_label: "Nomina / accordo trattamento dati", mancante: true },
            ]).map((doc) => {
              const meta = DOC_VALIDITA[doc.stato_validita] || DOC_VALIDITA.mancante;
              return (
                <div
                  key={doc.tipo}
                  className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-slate-900">{doc.tipo_label}</p>
                    <Badge variant="outline" className={`text-[10px] ${meta.className}`}>
                      {meta.label}
                    </Badge>
                  </div>
                  {doc.mancante || !doc.id ? (
                    <p className="mt-2 text-xs text-slate-500">Nessun documento caricato</p>
                  ) : (
                    <dl className="mt-2 space-y-1 text-xs text-slate-600">
                      <div className="truncate">
                        <span className="text-slate-400">File: </span>
                        {doc.ricevuto_cartaceo ? "Originale cartaceo registrato" : doc.file_name}
                      </div>
                      <div>
                        <span className="text-slate-400">Firma: </span>
                        {(STATO_FIRMA_OPTS.find((x) => x.id === doc.stato_firma) || {}).label ||
                          doc.stato_firma ||
                          "—"}
                      </div>
                      {doc.created_by_role ? (
                        <div>
                          <span className="text-slate-400">Caricato da: </span>
                          {doc.created_by_role === "struttura" ? "Struttura" : "Farmacia"}
                          {doc.created_by ? ` (${doc.created_by})` : ""}
                        </div>
                      ) : null}
                      <div>
                        <span className="text-slate-400">Data: </span>
                        {doc.data_documento || "—"}
                        {doc.scadenza ? ` · Scad. ${doc.scadenza}` : ""}
                      </div>
                      {doc.storico_count > 0 ? (
                        <div className="text-slate-400">Versioni precedenti: {doc.storico_count}</div>
                      ) : null}
                    </dl>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {doc.id ? (
                      <>
                        {!doc.ricevuto_cartaceo ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-lg"
                            disabled={docsBusy === `dl-${doc.id}`}
                            onClick={() => downloadDocumento(doc)}
                          >
                            <Download className="mr-1 h-3.5 w-3.5" />
                            Scarica
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          onClick={() =>
                            setUploadForm({
                              tipo: doc.tipo,
                              fascicoloId: null,
                              stato_firma: doc.stato_firma || "non_richiesta",
                              classe_validita: doc.classe_validita || "senza_scadenza",
                              data_documento: doc.data_documento || "",
                              scadenza: doc.scadenza || "",
                              note: doc.note || "",
                              file: null,
                              replaceId: doc.id,
                            })
                          }
                        >
                          Sostituisci
                        </Button>
                        {doc.classe_validita === "valido_fino_a_revoca" && !doc.revocato ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-lg text-rose-700"
                            disabled={docsBusy === `rv-${doc.id}`}
                            onClick={() => revocaDocumento(doc)}
                          >
                            Revoca
                          </Button>
                        ) : null}
                        <select
                          className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[11px]"
                          value={doc.stato_firma || "non_richiesta"}
                          disabled={docsBusy === `meta-${doc.id}` || doc.revocato}
                          onChange={(e) => patchDocumentoMeta(doc, { stato_firma: e.target.value })}
                        >
                          {STATO_FIRMA_OPTS.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          className="rounded-lg bg-[#4CAF50] hover:bg-[#43A047]"
                          onClick={() =>
                            setUploadForm({
                              tipo: doc.tipo,
                              fascicoloId: null,
                              stato_firma: "non_richiesta",
                              classe_validita: "senza_scadenza",
                              data_documento: "",
                              scadenza: "",
                              note: "",
                              file: null,
                              replaceId: null,
                            })
                          }
                        >
                          <FileUp className="mr-1 h-3.5 w-3.5" />
                          Carica
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-lg" disabled={docsBusy === `paper-${doc.tipo}`} onClick={() => registraCartaceo(doc)}>
                          Ricevuto cartaceo
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          className="h-10 rounded-xl border-slate-200 bg-white sm:max-w-xs"
          placeholder="Cerca ospite…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex flex-wrap gap-1.5">
          {FILTRI_STATO.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFiltro(f.id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                filtro === f.id
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {ospiti.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center">
          <Users className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-700">Nessun ospite</p>
          <p className="mt-1 text-xs text-slate-400">
            Gli ospiti compaiono se attivati in Deblistering dalla struttura.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {ospiti.map((o) => {
            const stato = o.stato || "nessuna_pratica";
            const meta = STATO_LABELS[stato] || STATO_LABELS.nessuna_pratica;
            const anomalie = o.anomalie || [];
            const anomalieDoc = o.anomalie_documentali || [];
            const ultima = o.ultima_attivita
              ? String(o.ultima_attivita).slice(0, 10)
              : null;
            return (
              <div
                key={o.fascicolo_id}
                className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900">
                      {o.ospite_cognome} {o.ospite_nome}
                    </h3>
                    {o.codice_fiscale ? (
                      <p className="mt-0.5 font-mono text-[11px] text-slate-500">
                        {o.codice_fiscale}
                      </p>
                    ) : null}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">
                        Servizio {o.stato_servizio === "attivo" ? "attivo" : (o.stato_servizio || "—")}
                      </Badge>
                      {o.struttura_nome || o.struttura ? (
                        <span className="truncate text-[11px] text-slate-400">
                          {o.struttura_nome || o.struttura}
                        </span>
                      ) : null}
                    </div>
                    {o.medico ? (
                      <p className="mt-0.5 text-xs text-slate-500">Medico: {o.medico}</p>
                    ) : null}
                    {ultima ? (
                      <p className="mt-0.5 text-[11px] text-slate-400">Ultima attività: {ultima}</p>
                    ) : null}
                    <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-slate-500">
                      {(o.carepy_count || 0) > 0 ? <span>Carepy: {o.carepy_count}</span> : null}
                      {(o.ricette_count || 0) > 0 ? <span>· Ricette: {o.ricette_count}</span> : null}
                      {(o.ordini_aperti || 0) > 0 ? <span>· Ordini aperti: {o.ordini_aperti}</span> : null}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {o.terapia_da_visionare ? (
                        <Badge className="border border-amber-200 bg-amber-50 text-[10px] text-amber-800">
                          Terapia da visionare
                        </Badge>
                      ) : null}
                      {(o.piani_scaduti || 0) > 0 ? (
                        <Badge className="border border-rose-200 bg-rose-50 text-[10px] text-rose-800">
                          {o.piani_scaduti} piani scaduti
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <Badge className={`border text-[10px] font-medium ${meta.className}`}>
                    {meta.label}
                  </Badge>
                </div>

                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                  <Step
                    done={!!o.terapia_presente && !o.terapia_da_visionare}
                    label="Terapia"
                    warning={!!o.terapia_da_visionare}
                  />
                  <Step done={!!o.richiesta_farmacia_ricevuta} label="Richiesta farmacia" />
                  <Step done={!!o.invio_richieste_medico} label="Invio al medico" />
                  <Step
                    done={!!o.ricette_medico_ricevute}
                    label="Ricette medico"
                    warning={anomalie.length > 0}
                  />
                  <Step done={!!o.invio_farmacia_effettuato} label="Invio farmacia" />
                </div>

                {(anomalie.length > 0 || anomalieDoc.length > 0) && (
                  <p className="mt-2 text-xs text-amber-700">
                    {anomalieDoc.length > 0
                      ? `${anomalieDoc.length} anomalie documentali${anomalieDoc.includes("delega_mancante") ? " (delega mancante)" : ""}`
                      : `${anomalie.length} anomalie rilevate`}
                  </p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={o.terapia_da_visionare ? "default" : "outline"}
                    className={`rounded-lg ${o.terapia_da_visionare ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                    disabled={loadingTerapia}
                    onClick={() => openTerapia(o)}
                  >
                    {loadingTerapia ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <Pill className="mr-1 h-3.5 w-3.5" />
                        Terapia
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-lg"
                    disabled={loadingPiani}
                    onClick={() => openPiani(o)}
                  >
                    {loadingPiani ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <FileText className="mr-1 h-3.5 w-3.5" />
                        Piani
                        {(o.piani_terapeutici_count || 0) > 0 ? ` (${o.piani_terapeutici_count})` : ""}
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`rounded-lg ${
                      (o.anomalie_documentali || []).includes("delega_mancante")
                        ? "border-amber-300 text-amber-800"
                        : ""
                    }`}
                    disabled={loadingDelega}
                    onClick={() => openDelega(o)}
                  >
                    {loadingDelega ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <ClipboardCheck className="mr-1 h-3.5 w-3.5" />
                        Delega
                      </>
                    )}
                  </Button>
                  <Button asChild size="sm" className="rounded-lg bg-[#4CAF50] hover:bg-[#43A047]">
                    <Link
                      to={`/farmacia/richieste?organization_id=${encodeURIComponent(orgId)}&fascicolo_id=${encodeURIComponent(o.fascicolo_id)}`}
                    >
                      <FileUp className="mr-1 h-3.5 w-3.5" />
                      Carepy
                    </Link>
                  </Button>
                  {o.sollecitabile && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      disabled={sending === o.fascicolo_id}
                      onClick={() => sollecita(o)}
                    >
                      {sending === o.fascicolo_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <Send className="mr-1 h-3.5 w-3.5" />
                          Sollecita
                        </>
                      )}
                    </Button>
                  )}
                  {!String(o.id || "").startsWith("nopratica_") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-lg text-slate-600"
                      disabled={loadingPratica}
                      onClick={() => openPratica(o)}
                    >
                      Pratica
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {terapiaDetail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Terapia (sola lettura)</h3>
                <p className="text-xs text-slate-500">
                  {(terapiaDetail.ospite_cognome || "")} {(terapiaDetail.ospite_nome || "")}
                  {terapiaDetail.codice_fiscale ? ` · ${terapiaDetail.codice_fiscale}` : ""}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setTerapiaDetail(null)}>
                Chiudi
              </Button>
            </div>

            <div className="mb-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {terapiaDetail.presa_visione?.stato === "da_visionare" && (
                <p className="font-medium text-amber-800">
                  Nuova presa visione richiesta (la struttura ha aggiornato la terapia).
                </p>
              )}
              {terapiaDetail.presa_visione?.stato === "visionata" && (
                <p>
                  Visionata il{" "}
                  <span className="font-medium">
                    {String(terapiaDetail.presa_visione.visionata_at || "").replace("T", " ").slice(0, 19)}
                  </span>
                  {terapiaDetail.presa_visione.visionata_by_nome
                    ? ` da ${terapiaDetail.presa_visione.visionata_by_nome}`
                    : ""}
                </p>
              )}
              {terapiaDetail.presa_visione?.stato === "nessuna_terapia" && (
                <p>Nessuna terapia in corso per questo ospite.</p>
              )}
            </div>

            {(terapiaDetail.items || []).length === 0 ? (
              <p className="text-sm text-slate-500">Nessun farmaco in terapia.</p>
            ) : (
              <div className="space-y-3">
                {(terapiaDetail.items || []).map((t) => (
                  <div
                    key={t.id || t.farmaco}
                    className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-slate-900">{t.farmaco || "—"}</p>
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          t.stato === "attiva"
                            ? "border-emerald-200 text-emerald-700"
                            : "border-slate-200 text-slate-500"
                        }`}
                      >
                        {t.stato || "—"}
                      </Badge>
                    </div>
                    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                      <div>
                        <dt className="text-slate-400">Dosaggio</dt>
                        <dd>{t.dosaggio || "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-400">Frequenza</dt>
                        <dd>{t.frequenza || "—"}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-slate-400">Orari</dt>
                        <dd>{(t.orari || []).length ? t.orari.join(", ") : "—"}</dd>
                      </div>
                      {t.note ? (
                        <div className="col-span-2">
                          <dt className="text-slate-400">Note</dt>
                          <dd>{t.note}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt className="text-slate-400">Data inizio</dt>
                        <dd>{t.data_inizio ? String(t.data_inizio).slice(0, 10) : "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-400">Ultima modifica</dt>
                        <dd>{t.data_ultima_modifica ? String(t.data_ultima_modifica).slice(0, 19).replace("T", " ") : "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-400">Autore</dt>
                        <dd>{t.autore_ultima_modifica || "—"}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            )}

            {terapiaDetail.presa_visione?.da_visionare && (
              <Button
                className="mt-4 w-full rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
                disabled={savingPresa}
                onClick={registraPresaVisione}
              >
                {savingPresa ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Eye className="mr-2 h-4 w-4" />
                )}
                Registra presa visione
              </Button>
            )}

            {(terapiaDetail.storico || []).length > 0 ? (
              <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Storico prese visione
                </h4>
                <ul className="space-y-2">
                  {(terapiaDetail.storico || []).map((s) => (
                    <li key={s.id || s.visionata_at} className="text-xs text-slate-600">
                      <span className="font-medium text-slate-800">
                        {String(s.visionata_at || "").replace("T", " ").slice(0, 19) || "—"}
                      </span>
                      {s.visionata_by_nome ? ` · ${s.visionata_by_nome}` : ""}
                      {s.terapie_count != null ? ` · ${s.terapie_count} terapie` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="mt-3 text-[11px] text-slate-400">
              Solo consultazione: non è possibile aggiungere, modificare o sospendere terapie dal portale.
            </p>
          </div>
        </div>
      )}

      {pianiDetail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Piani terapeutici</h3>
                <p className="text-xs text-slate-500">
                  {(pianiDetail.ospite_cognome || "")} {(pianiDetail.ospite_nome || "")}
                  {pianiDetail.codice_fiscale ? ` · ${pianiDetail.codice_fiscale}` : ""}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  Stessi documenti del Fascicolo → Sanitario (sola lettura)
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPianiDetail(null)}>
                Chiudi
              </Button>
            </div>

            {(pianiDetail.items || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                <FileText className="mx-auto mb-2 h-7 w-7 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">Nessun piano terapeutico</p>
                <p className="mt-1 text-xs text-slate-400">
                  L&apos;assenza di un piano non è un errore. Puoi comunque richiedere un aggiornamento alla struttura.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {(pianiDetail.items || []).map((p) => {
                  const meta = PIANO_STATO[p.stato] || PIANO_STATO.senza_scadenza;
                  return (
                    <div
                      key={p.file_id}
                      className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-slate-900">{p.titolo || "Piano terapeutico"}</p>
                        <Badge variant="outline" className={`text-[10px] ${meta.className}`}>
                          {meta.label}
                        </Badge>
                      </div>
                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                        <div>
                          <dt className="text-slate-400">Farmaco associato</dt>
                          <dd>{p.farmaco_associato || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-slate-400">Data</dt>
                          <dd>{p.data ? String(p.data).slice(0, 10) : "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-slate-400">Scadenza</dt>
                          <dd>{p.scadenza || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-slate-400">File</dt>
                          <dd className="truncate">{p.file_name || "—"}</dd>
                        </div>
                      </dl>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          disabled={pianiBusy === `dl-${p.file_id}`}
                          onClick={() => downloadPiano(p)}
                        >
                          {pianiBusy === `dl-${p.file_id}` ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <>
                              <Download className="mr-1 h-3.5 w-3.5" />
                              Scarica
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg text-amber-800"
                          disabled={pianiBusy === `sc-${p.file_id}`}
                          onClick={() => segnalaPianoScaduto(p)}
                        >
                          {pianiBusy === `sc-${p.file_id}` ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <>
                              <AlertTriangle className="mr-1 h-3.5 w-3.5" />
                              Segnala scaduto
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Button
              className="mt-4 w-full rounded-xl"
              variant="outline"
              disabled={pianiBusy === "upd"}
              onClick={richiediAggiornamentoPiani}
            >
              {pianiBusy === "upd" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Richiedi aggiornamento alla struttura
            </Button>
            <p className="mt-3 text-[11px] text-slate-400">
              Non è possibile caricare, modificare, sostituire o eliminare piani dal Portale Farmacia.
            </p>
          </div>
        </div>
      )}

      {praticaDetail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Resoconto pratica</h3>
                <p className="text-xs text-slate-500">
                  {(praticaDetail.pratica?.ospite_cognome || "")}{" "}
                  {(praticaDetail.pratica?.ospite_nome || "")}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPraticaDetail(null)}>
                Chiudi
              </Button>
            </div>
            <div className="space-y-2 text-sm">
              <p>
                Stato:{" "}
                <span className="font-medium">
                  {(praticaDetail.pratica?.stato || "").replace(/_/g, " ")}
                </span>
              </p>
              <p>
                Richieste: {praticaDetail.richieste?.length || 0} · Ricette:{" "}
                {praticaDetail.ricette?.length || 0}
              </p>
              {praticaDetail.confronto && (
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs">
                  <p>
                    Completo:{" "}
                    {praticaDetail.confronto.completo ? (
                      <span className="text-emerald-700">Sì</span>
                    ) : (
                      <span className="text-amber-700">No</span>
                    )}
                  </p>
                  {(praticaDetail.confronto.mancanti_in_ricette || []).length > 0 && (
                    <p className="mt-1">
                      Mancanti: {(praticaDetail.confronto.mancanti_in_ricette || []).join(", ")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {delegaDetail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Delega</h3>
                <p className="text-xs text-slate-500">
                  {(delegaDetail.ospite_cognome || "")} {(delegaDetail.ospite_nome || "")}
                  {delegaDetail.codice_fiscale ? ` · ${delegaDetail.codice_fiscale}` : ""}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDelegaDetail(null)}>
                Chiudi
              </Button>
            </div>
            {delegaDetail.delega?.mancante ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
                <p className="text-sm font-medium text-slate-700">Delega mancante</p>
                <p className="mt-1 text-xs text-slate-400">
                  Puoi caricare una copia dal portale se non è ancora firmata in Affidya.
                </p>
                <Button
                  className="mt-3 rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
                  onClick={() =>
                    setUploadForm({
                      tipo: "delega",
                      fascicoloId: delegaDetail.fascicolo_id,
                      stato_firma: "firmato",
                      classe_validita: "valido_fino_a_revoca",
                      data_documento: "",
                      scadenza: "",
                      note: "",
                      file: null,
                      replaceId: null,
                    })
                  }
                >
                  <FileUp className="mr-1 h-4 w-4" />
                  Carica delega
                </Button>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-slate-900">
                    {delegaDetail.delega?.file_name || "Delega"}
                  </p>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      (DOC_VALIDITA[delegaDetail.delega?.stato_validita] || DOC_VALIDITA.senza_scadenza)
                        .className
                    }`}
                  >
                    {(DOC_VALIDITA[delegaDetail.delega?.stato_validita] || DOC_VALIDITA.senza_scadenza)
                      .label}
                  </Badge>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Fonte:{" "}
                  {delegaDetail.has_delega_struttura
                    ? "firma Affidya (sola lettura)"
                    : "archivio Portale Farmacia"}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Data: {delegaDetail.delega?.data_documento || "—"} · Firma:{" "}
                  {delegaDetail.delega?.stato_firma || "—"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-lg"
                    disabled={!!docsBusy}
                    onClick={() => downloadDocumento(delegaDetail.delega, { delega: true })}
                  >
                    <Download className="mr-1 h-3.5 w-3.5" />
                    Scarica
                  </Button>
                  {delegaDetail.can_upload && delegaDetail.delega?.id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      onClick={() =>
                        setUploadForm({
                          tipo: "delega",
                          fascicoloId: delegaDetail.fascicolo_id,
                          stato_firma: delegaDetail.delega.stato_firma || "firmato",
                          classe_validita:
                            delegaDetail.delega.classe_validita || "valido_fino_a_revoca",
                          data_documento: delegaDetail.delega.data_documento || "",
                          scadenza: delegaDetail.delega.scadenza || "",
                          note: "",
                          file: null,
                          replaceId: delegaDetail.delega.id,
                        })
                      }
                    >
                      Sostituisci
                    </Button>
                  )}
                </div>
              </div>
            )}
            <p className="mt-3 text-[11px] text-slate-400">
              Se la delega è già firmata in Affidya non si può sostituire dal portale. Non è un motore di
              firma digitale.
            </p>
          </div>
        </div>
      )}

      {uploadForm.tipo && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {uploadForm.replaceId ? "Sostituisci documento" : "Carica documento"}
                </h3>
                <p className="text-xs text-slate-500">
                  {uploadForm.tipo === "delega"
                    ? "Delega ospite"
                    : (docsStruttura?.items || []).find((d) => d.tipo === uploadForm.tipo)
                        ?.tipo_label || uploadForm.tipo}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setUploadForm((f) => ({ ...f, tipo: null, file: null, replaceId: null, fascicoloId: null }))
                }
              >
                Chiudi
              </Button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="mb-1 block text-xs text-slate-500">File</label>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, file: e.target.files?.[0] || null }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs text-slate-500">Stato firma</label>
                  <select
                    className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm"
                    value={uploadForm.stato_firma}
                    onChange={(e) => setUploadForm((f) => ({ ...f, stato_firma: e.target.value }))}
                  >
                    {STATO_FIRMA_OPTS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-500">Validità</label>
                  <select
                    className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm"
                    value={uploadForm.classe_validita}
                    onChange={(e) =>
                      setUploadForm((f) => ({ ...f, classe_validita: e.target.value }))
                    }
                  >
                    {CLASSE_VALIDITA_OPTS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs text-slate-500">Data documento</label>
                  <Input
                    type="date"
                    value={uploadForm.data_documento}
                    onChange={(e) =>
                      setUploadForm((f) => ({ ...f, data_documento: e.target.value }))
                    }
                  />
                </div>
                {uploadForm.classe_validita === "con_scadenza" && (
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Scadenza</label>
                    <Input
                      type="date"
                      value={uploadForm.scadenza}
                      onChange={(e) => setUploadForm((f) => ({ ...f, scadenza: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Note</label>
                <Input
                  value={uploadForm.note}
                  onChange={(e) => setUploadForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Opzionale"
                />
              </div>
              <Button
                className="w-full rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
                disabled={docsBusy === "upload" || String(docsBusy || "").startsWith("rp-")}
                onClick={() =>
                  uploadForm.replaceId
                    ? replaceDocumento({
                        id: uploadForm.replaceId,
                        stato_firma: uploadForm.stato_firma,
                        classe_validita: uploadForm.classe_validita,
                      })
                    : submitUploadDocumento()
                }
              >
                {docsBusy === "upload" || String(docsBusy || "").startsWith("rp-") ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {uploadForm.replaceId ? "Conferma sostituzione" : "Carica"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
